import { addMinutes, differenceInMinutes } from "date-fns";

export interface ChoghadiyaInterval {
  startTime: Date;
  endTime: Date;
  name: string;
  romanianName: string;
  quality: "auspicious" | "inauspicious" | "good";
  rulingPlanet: string;
  romanianDescription: string;
  activities: string;
}

// Choghadiya names and their qualities
const choghadiyaTypes = [
  {
    name: "Udveg",
    romanianName: "Udveg",
    quality: "inauspicious" as const,
    rulingPlanet: "Soare",
    romanianDescription: "Perioada Udveg este asociată cu energiile neliniștii și agitației. Este totuși favorabilă pentru activități legate de guvern sau politică.",
    activities: "Activități legate de guvern sau politică"
  },
  {
    name: "Amrit",
    romanianName: "Amrit",
    quality: "auspicious" as const,
    rulingPlanet: "Luna",
    romanianDescription: "Perioada Amrit este foarte favorabilă pentru toate tipurile de activități. În special legate de relații și pentru realizarea de planuri de afaceri și profesionale.",
    activities: "Bună pentru toate tipurile de activități"
  },
  {
    name: "Rog",
    romanianName: "Rog",
    quality: "inauspicious" as const,
    rulingPlanet: "Marte",
    romanianDescription: "Perioada Rog este nefavorabilă și poate aduce probleme de sănătate sau conflicte. Evitați începerea de noi proiecte în acest interval. Favorabilă totuți pentru activități legate de competiții și de învingerea dușmanilor.",
    activities: "Războaie, lupte, dezbateri, competiții"
  },
  {
    name: "Labh",
    romanianName: "Labh",
    quality: "auspicious" as const,
    rulingPlanet: "Mercur",
    romanianDescription: "Perioada Labh este asociată cu profitul și câștigul. Este un moment bun pentru începerea de noi afaceri, semnarea de contracte, pentru job și educație.",
    activities: "Începerea unei noi afaceri, job sau educație"
  },
  {
    name: "Shubh",
    romanianName: "Shubh",
    quality: "auspicious" as const,
    rulingPlanet: "Jupiter",
    romanianDescription: "Perioada Shubh este foarte favorabilă și aduce noroc în începerea de noi afaceri, căsătorii și activități religioase, și în începerea unor călătorii mult visate. Favorabilă de asemenea pentru a cumpăra aur și grâne.",
    activities: "Ritualuri de căsătorie / Activități religioase"
  },
  {
    name: "Char",
    romanianName: "Char",
    quality: "good" as const,
    rulingPlanet: "Venus",
    romanianDescription: "Perioada Char este cea mai bună pentru călătorii. Este de asemenea un moment favorabil pentru investiții și activități legate de iubire și frumusețe.",
    activities: "Călătorii"
  },
  {
    name: "Kaal",
    romanianName: "Kaal",
    quality: "inauspicious" as const,
    rulingPlanet: "Saturn",
    romanianDescription: "Perioada Kaal este foarte nefavorabilă și poate aduce obstacole și întârzieri. Evitați începerea de noi proiecte și realizarea de ritualuri religioase (pentru țeluri lumești) în acest interval. Unele activități financiare pot fi totuși favorizate.",
    activities: "Activități legate de finanțe"
  }
];

// Day sequence for each day of the week (starting from Sunday)
// Based on the provided table image
const daySequence = [
  ["Udveg", "Char", "Labh", "Amrit", "Kaal", "Shubh", "Rog", "Udveg"], // Sunday
  ["Amrit", "Kaal", "Shubh", "Rog", "Udveg", "Char", "Labh", "Amrit"], // Monday
  ["Rog", "Udveg", "Char", "Labh", "Amrit", "Kaal", "Shubh", "Rog"], // Tuesday
  ["Labh", "Amrit", "Kaal", "Shubh", "Rog", "Udveg", "Char", "Labh"], // Wednesday
  ["Shubh", "Rog", "Udveg", "Char", "Labh", "Amrit", "Kaal", "Shubh"], // Thursday
  ["Char", "Labh", "Amrit", "Kaal", "Shubh", "Rog", "Udveg", "Char"], // Friday
  ["Kaal", "Shubh", "Rog", "Udveg", "Char", "Labh", "Amrit", "Kaal"]  // Saturday
];

// Night sequence for each day of the week (starting from Sunday)
// Based on the provided table image
const nightSequence = [
  ["Shubh", "Amrit", "Char", "Rog", "Kaal", "Labh", "Udveg", "Shubh"], // Sunday
  ["Char", "Rog", "Kaal", "Labh", "Udveg", "Shubh", "Amrit", "Char"], // Monday
  ["Kaal", "Labh", "Udveg", "Shubh", "Amrit", "Char", "Rog", "Kaal"], // Tuesday
  ["Udveg", "Shubh", "Amrit", "Char", "Rog", "Kaal", "Labh", "Udveg"], // Wednesday
  ["Amrit", "Char", "Rog", "Kaal", "Labh", "Udveg", "Shubh", "Amrit"], // Thursday
  ["Rog", "Kaal", "Labh", "Udveg", "Shubh", "Amrit", "Char", "Rog"], // Friday
  ["Labh", "Udveg", "Shubh", "Amrit", "Char", "Rog", "Kaal", "Labh"]  // Saturday
];

// Each sequence already has 8 elements for the 8 intervals

export const calculateChoghadiyaIntervals = (
  sunrise: Date,
  sunset: Date,
  nextSunrise: Date
): ChoghadiyaInterval[] => {
  const dayOfWeek = sunrise.getDay(); // 0 for Sunday, 1 for Monday, etc.

  // Get the appropriate sequences for the day
  const dayChoghadiyaSequence = daySequence[dayOfWeek];
  const nightChoghadiyaSequence = nightSequence[dayOfWeek];

  // Calculate duration of day and night in minutes
  const dayDurationMinutes = differenceInMinutes(sunset, sunrise);
  const nightDurationMinutes = differenceInMinutes(nextSunrise, sunset);

  // Calculate duration of each interval
  const dayIntervalMinutes = Math.floor(dayDurationMinutes / 8);
  const nightIntervalMinutes = Math.floor(nightDurationMinutes / 8);

  const choghadiyaIntervals: ChoghadiyaInterval[] = [];

  // Calculate day intervals
  for (let i = 0; i < 8; i++) {
    const startTime = addMinutes(sunrise, i * dayIntervalMinutes);
    const endTime = i < 7
      ? addMinutes(sunrise, (i + 1) * dayIntervalMinutes)
      : sunset; // Last interval ends at sunset

    const choghadiyaType = choghadiyaTypes.find(
      type => type.name === dayChoghadiyaSequence[i]
    );

    if (choghadiyaType) {
      choghadiyaIntervals.push({
        startTime,
        endTime,
        name: choghadiyaType.name,
        romanianName: choghadiyaType.romanianName,
        quality: choghadiyaType.quality,
        rulingPlanet: choghadiyaType.rulingPlanet,
        romanianDescription: choghadiyaType.romanianDescription,
        activities: choghadiyaType.activities
      });
    }
  }

  // Calculate night intervals
  for (let i = 0; i < 8; i++) {
    const startTime = addMinutes(sunset, i * nightIntervalMinutes);
    const endTime = i < 7
      ? addMinutes(sunset, (i + 1) * nightIntervalMinutes)
      : nextSunrise; // Last interval ends at next sunrise

    const choghadiyaType = choghadiyaTypes.find(
      type => type.name === nightChoghadiyaSequence[i]
    );

    if (choghadiyaType) {
      choghadiyaIntervals.push({
        startTime,
        endTime,
        name: choghadiyaType.name,
        romanianName: choghadiyaType.romanianName,
        quality: choghadiyaType.quality,
        rulingPlanet: choghadiyaType.rulingPlanet,
        romanianDescription: choghadiyaType.romanianDescription,
        activities: choghadiyaType.activities
      });
    }
  }

  return choghadiyaIntervals;
};

export const getCurrentChoghadiya = (
  choghadiyaIntervals: ChoghadiyaInterval[],
  currentTime: Date
): ChoghadiyaInterval | null => {
  return (
    choghadiyaIntervals.find(
      interval =>
        currentTime >= interval.startTime && currentTime < interval.endTime
    ) || null
  );
};
